/**
 * Ressources vidéo officielles — Sécurité routière France
 *
 * Architecture : on privilégie les PLAYLISTS officielles plutôt que des IDs de vidéos individuels
 * pour 2 raisons :
 *   1. Pérennité : une vidéo peut être supprimée ; une playlist officielle reste
 *   2. Richesse : l'utilisateur accède à tout le corpus autour du thème
 *
 * Toutes les vidéos sont intégrées via l'API officielle YouTube (mode youtube-nocookie),
 * ce qui est 100 % autorisé par les CGU YouTube tant que :
 *   - On ne copie / ne ré-héberge pas le contenu
 *   - On crédite la source (ici : chaîne officielle Sécurité routière France)
 *   - On ne monétise pas directement leurs vidéos
 *
 * Chaîne officielle : https://www.youtube.com/@securiteroutiere
 * Délégation à la Sécurité routière (ministère de l'Intérieur).
 */

export const OFFICIAL_CHANNELS = [
  {
    name: 'Sécurité routière',
    handle: '@securiteroutiere',
    url: 'https://www.youtube.com/@securiteroutiere',
    description: 'Chaîne officielle de la Délégation à la sécurité routière (ministère de l\'Intérieur)',
    subscribers: '69 000+',
    videoCount: '580+',
  },
  {
    name: 'Fondation VINCI Autoroutes',
    handle: '@FondationVINCIAutoroutes',
    url: 'https://www.youtube.com/@FondationVINCIAutoroutes',
    description: 'Prévention routière, sécurité sur autoroute, études scientifiques',
    subscribers: '18 000+',
    videoCount: '200+',
  },
  {
    name: 'Prévention Routière',
    handle: '@preventionroutiere',
    url: 'https://www.youtube.com/@preventionroutiere',
    description: 'Association loi 1901 — reconnue d\'utilité publique depuis 1954',
    subscribers: '8 000+',
    videoCount: '300+',
  },
];

/**
 * Playlists thématiques officielles, par thème ETM.
 * Format : { youtubeListId, title, channel, description, themes: ['L', 'S', ...] }
 *
 * Les playlists ci-dessous sont PUBLIQUES et INTÉGRABLES selon les CGU YouTube.
 * En cas de modification de la chaîne officielle, remplacer les IDs dans ce fichier seulement.
 */
export const OFFICIAL_PLAYLISTS = [
  {
    id: 'parlons-secu-routiere',
    listId: 'PLKAhYoOVYE-GtrFmLIIIZKdfeA1YnP-c7',
    title: 'Parlons sécurité routière',
    channel: 'Sécurité routière',
    description: 'Série pédagogique officielle : jeunes, inattention, alcool & stupéfiants, vitesse…',
    themes: ['L', 'S', 'U'],
    kicker: 'Série phare',
  },
  {
    id: 'prevention-routiere',
    listId: 'PLQkgJ20cc8VkHUR9JdaNrzICXupRvBbcS',
    title: 'Prévention routière — campagnes',
    channel: 'Sécurité routière',
    description: 'Campagnes chocs sur l\'alcool, les stupéfiants et les comportements à risque',
    themes: ['L', 'S'],
    kicker: 'Campagnes nationales',
  },
];

/**
 * Vidéos thématiques individuelles — si vous connaissez l'ID d'une vidéo précise.
 * Format : { videoId (YouTube), title, channel, themes, duration }
 *
 * IMPORTANT : ajoutez ici les IDs de vidéos officielles que vous souhaitez mettre en avant
 * sur la plateforme (depuis le bouton "Partager" YouTube sur une vidéo publique officielle).
 * Tout ajout doit provenir des 3 chaînes officielles listées dans OFFICIAL_CHANNELS.
 */
export const OFFICIAL_VIDEOS = [
  {
    videoId: '5E-ITHJPj1k',
    title: 'Trajectoire de sécurité en virage — méthode E-I-E',
    channel: 'Sécurité routière',
    channelUrl: 'https://www.youtube.com/@securiteroutiere',
    themes: ['C', 'R'],
    category: 'trajectoire',
    duration: '3:00',
    description: "Vidéo pédagogique officielle : comprendre la trajectoire de sécurité en virage, la méthode Élargir-Intérieur-Extérieur (E-I-E) et l'importance du placement sur la chaussée. Complément aux dossiers magazine et enquête E-I-E sur CODE 2 ROUES.",
  },
  {
    videoId: 'Z4V36NFlRTM',
    title: "À moto, ne soyez pas victime de votre passion (version longue)",
    channel: 'Sécurité routière',
    channelUrl: 'https://www.youtube.com/@securiteroutiere',
    themes: ['L', 'P'],
    category: 'campagne-2026',
    duration: '1:50',
    publishedAt: '2026-06-04',
    isCampaign: true,
    campaignSlug: 'campagne-2026-motards-experimentes',
    description: "Nouvelle campagne 2026 de la Sécurité routière à destination des motards expérimentés. Portrait de Julien, motard et père, qui se raconte : l'appel de la route, le désir paradoxal de vitesse, la peur de l'accélération de trop. Réalisé par Alban Teurlay et Thierry Demaizière. 72 % des motards tués sont des usagers expérimentés sur moto lourde, lors d'un trajet de loisir.",
  },
  {
    videoId: 'bzNcQX1IuLk',
    title: "À moto, ne soyez pas victime de votre passion (version courte)",
    channel: 'Sécurité routière',
    channelUrl: 'https://www.youtube.com/@securiteroutiere',
    themes: ['L', 'P'],
    category: 'campagne-2026',
    duration: '0:35',
    publishedAt: '2026-06-04',
    isCampaign: true,
    campaignSlug: 'campagne-2026-motards-experimentes',
    description: "Version courte (35s) du spot de la campagne 2026 Sécurité routière. Un rituel : embrasser ses enfants avant chaque sortie moto, parce que tout peut arriver en un instant.",
  },
  {
    videoId: 'SRqGhOjijpc',
    title: "À moto ou à scooter, sans équipement complet, vous risquez d'y laisser votre peau",
    channel: 'Sécurité routière',
    channelUrl: 'https://www.youtube.com/@securiteroutiere',
    themes: ['L', 'P'],
    category: 'equipement',
    description: "Campagne choc illustrant les conséquences d'une chute à moto sans équipement adapté. Complément vidéo au simulateur officiel #EquipezVous.",
  },
  {
    videoId: '25o2wDEi08k',
    title: "Proto : on passe vite du rire aux drames",
    channel: 'Sécurité routière',
    channelUrl: 'https://www.youtube.com/@securiteroutiere',
    themes: ['L', 'P', 'M'],
    category: 'debridage',
    description: "Campagne sur les deux-roues débridés ou modifiés (« protos ») : ce qui paraît fun tourne au drame. Rappel : débrider un 2-roues est illégal (sanctions pénales et invalidité de l'assurance).",
  },
  {
    videoId: 'Nkzb2MiBUjU',
    title: "Julien Febreau — Quand on tient à quelqu'un, on le retient !",
    channel: 'Sécurité routière',
    channelUrl: 'https://www.youtube.com/@securiteroutiere',
    themes: ['P', 'U'],
    category: 'prevention',
    description: "Le commentateur F1 Julien Febreau prend la parole pour la Sécurité routière : quand un proche s'apprête à prendre la route dans un état qui le rend dangereux (alcool, fatigue, stupéfiants), c'est à nous de le retenir.",
  },
  {
    videoId: '8aD5TGtpEZg',
    title: "La nuit, ne soyez pas celui qu'on avait pas vu",
    channel: 'Sécurité routière',
    channelUrl: 'https://www.youtube.com/@securiteroutiere',
    themes: ['P', 'U', 'L'],
    category: 'visibilite',
    description: "Campagne sur la visibilité de nuit : sans équipement réfléchissant ni bon éclairage, un usager vulnérable (cycliste, motard) reste invisible jusqu'au dernier instant. Rappel de l'importance du gilet haute visibilité et des dispositifs rétro-réfléchissants.",
  },
];

/**
 * Thèmes ETM pour filtrage
 */
export const ETM_THEMES = {
  C: 'La circulation routière',
  R: 'La route',
  U: 'Les autres usagers',
  L: 'Réglementation & équipements',
  S: 'Premiers secours',
  P: 'La prise de conscience des risques',
  E: 'L\'environnement',
  M: 'La mécanique',
  D: 'Les droits et devoirs',
};

export default { OFFICIAL_CHANNELS, OFFICIAL_PLAYLISTS, OFFICIAL_VIDEOS, ETM_THEMES };

/**
 * Métadonnées de la campagne en cours mise en avant (hero + page dédiée).
 * Mettre à jour cette constante à chaque nouvelle campagne pour rafraîchir
 * automatiquement le bandeau d'accueil et la page d'édito.
 */
export const CURRENT_CAMPAIGN = {
  slug: 'campagne-2026-motards-experimentes',
  kicker: 'Nouvelle campagne nationale · Sécurité routière 2026',
  title: 'À moto, ne soyez pas victime de votre passion',
  subtitle:
    "72 % des motards tués sont des usagers expérimentés, sur moto lourde, lors d'un trajet de loisir. La campagne 2026 s'adresse à eux.",
  channel: 'Sécurité routière',
  channelUrl: 'https://www.youtube.com/@securiteroutiere',
  publishedAt: '2026-06-04',
  directors: 'Alban Teurlay & Thierry Demaizière',
  heroVideoId: 'Z4V36NFlRTM', // version longue (1:50)
  shortVideoId: 'bzNcQX1IuLk', // version courte (0:35)
  heroVideoDuration: '1:50',
  shortVideoDuration: '0:35',
};

